// The image behind the canvas: fetched with the Authorization header (an <img> cannot send one),
// decoded off the main thread, and kept for the last few images so the next and previous are instant.
import { imageBlob } from "../api/annotations";

export interface Loaded { bitmap: ImageBitmap; width: number; height: number }

export class ImageCache {
  private done = new Map<number, Loaded>();
  private inflight = new Map<number, Promise<Loaded>>();

  constructor(
    private fetchBlob: (id: number, signal?: AbortSignal) => Promise<Blob> = imageBlob,
    private decode: (b: Blob) => Promise<ImageBitmap> = (b) => createImageBitmap(b),
    private max = 4,
  ) {}

  get(id: number, signal?: AbortSignal): Promise<Loaded> {
    const hit = this.done.get(id);
    if (hit) { this.done.delete(id); this.done.set(id, hit); return Promise.resolve(hit); }
    const pending = this.inflight.get(id);
    if (pending) return pending;
    const p = this.fetchBlob(id, signal).then(this.decode).then((bitmap) => {
      const loaded = { bitmap, width: bitmap.width, height: bitmap.height };
      this.done.set(id, loaded);
      while (this.done.size > this.max) {
        const [oldest, l] = this.done.entries().next().value as [number, Loaded];
        this.done.delete(oldest);
        l.bitmap.close?.();
      }
      return loaded;
    }).finally(() => this.inflight.delete(id));
    this.inflight.set(id, p);
    return p;
  }

  /** Start fetching an image the person is likely to open next. Errors are ignored. */
  prefetch(id: number) { void this.get(id).catch(() => {}); }

  forget(id: number) { const l = this.done.get(id); l?.bitmap.close?.(); this.done.delete(id); }
}

export const images = new ImageCache();
